<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import DashboardAppFrame from 'dashboard/components/widgets/DashboardApp/Frame.vue';

const route = useRoute();
const store = useStore();
const { t } = useI18n();

const apps = useMapGetter('dashboardApps/getRecords');
const isLoading = ref(true);
const app = computed(() =>
  apps.value.find(item => item.id === Number(route.params.appId))
);

// DashboardAppFrame only mounts its iframe after it has SEEN isVisible flip false → true.
// The app record can arrive async (store fetch), so the flip must happen after the Frame exists:
// whenever the resolved app changes, reset to false and flip on the next tick.
const isVisible = ref(false);
watch(
  app,
  value => {
    if (!value) return;
    isVisible.value = false;
    nextTick(() => {
      isVisible.value = true;
    });
  },
  { immediate: true }
);

onMounted(async () => {
  try {
    await store.dispatch('dashboardApps/get');
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <section class="flex flex-col w-full h-full bg-n-surface-1">
    <header
      v-if="app"
      class="flex items-center px-6 py-4 text-xl font-medium shrink-0 text-n-slate-12"
    >
      {{ app.title }}
    </header>
    <div class="relative flex-1 min-h-0 [&>div]:h-full [&_iframe]:h-full">
      <DashboardAppFrame
        v-if="app"
        :key="app.id"
        :config="app.content"
        :position="0"
        :is-visible="isVisible"
      />
      <p v-else-if="!isLoading" class="p-6 text-sm text-n-slate-11">
        {{ t('WASAPI_APPS.NOT_FOUND') }}
      </p>
    </div>
  </section>
</template>
